/**
 * useBlockingTracker — opt-in global loading counter for plain-fetch calls.
 *
 * React Query's useIsFetching/useIsMutating only track queries/mutations
 * registered with the QueryClient. Direct fleetApi calls that should block
 * the UI can opt in here:
 *
 *   const trackAsync = useTrackAsync();
 *   const result = await trackAsync(api.someCall(token));
 *
 * LoadingOverlay reads the counter via useBlockingCount() and includes it
 * in its isActive check.
 */

import { createContext, useContext, useState, useCallback } from 'react';

const CountCtx = createContext(0);
const SetCtx   = createContext(null);

export function BlockingProvider({ children }) {
  const [count, setCount] = useState(0);
  return (
    <CountCtx.Provider value={count}>
      <SetCtx.Provider value={setCount}>
        {children}
      </SetCtx.Provider>
    </CountCtx.Provider>
  );
}

/** Returns the current number of in-flight blocking calls. */
export const useBlockingCount = () => useContext(CountCtx);

/**
 * Returns a `trackAsync(promise)` function. Wrap any plain-fetch call that
 * should contribute to the global loading overlay:
 *
 *   const trackAsync = useTrackAsync();
 *   await trackAsync(fleetApi.someOp(token));
 */
export function useTrackAsync() {
  const setCount = useContext(SetCtx);
  return useCallback(async (promise) => {
    if (!setCount) return promise;   // outside provider — pass-through silently
    setCount(c => c + 1);
    try     { return await promise; }
    finally { setCount(c => c - 1); }
  }, [setCount]);
}
